/**
 * The On Body session — what is on the body, where, and what to put back.
 *
 * Module state, one per viewer client, NOT panel state: the left panel unmounts
 * the moment it is closed or another panel opens, and On Body stays on in the
 * viewer when that happens (the user may well be off exporting the picture).
 * The session keeps listening while the panel is gone: a new piece re-placed on
 * the same anchor, a gizmo or resize re-map reflected in the readouts.
 *
 * The viewer owns the placement (camera, container transform, occluder); this
 * owns only the host's half — which photo, which anchor, the backdrop that was
 * there before, and the Adjust readouts. Every write goes through the v58/v63
 * RPCs and is read back, so a readout never shows a number the viewer does not
 * hold.
 *
 * Not recorded in the studio's Undo history on purpose: placement is a
 * transient view (nothing of it is saved with the scene), and an Undo landing
 * after Clear would move the model container that `clearOnBody` just put back.
 */
import { useSyncExternalStore } from "react";
import {
  MODEL_ROOT_NODE_ID,
  type BackgroundImageFraming,
  type OnBodyAnchor,
  type OnBodyKind,
  type RingClientApi,
  type Vec3,
} from "../../../shared/api-contract";
import type { ViewerClient } from "../../../client/ViewerClient";
import { capsFor } from "../caps";
import {
  ALONG_DEFAULT,
  CLICKS,
  fingerAnchor,
  fingersOf,
  manualHand,
  manualNeckAnchor,
  measureFingerBetween,
  toAnchor,
  type FingerName,
  type PhotoPlacement,
  type PlacedHand,
  type Vec2,
} from "./anchorMath";
import { DEFAULT_BACKGROUND_FRAMING, fullFraming } from "./framing";
import { detectHands, measureImage, readPhotoPixels, type PhotoPixels } from "./detectHands";
import { aboutPivot, localOf, turnAboutWorldAxis, type NodeTransform } from "./transform";

/** How an anchor came to be — decides what the status line promises. */
export type PlaceMethod =
  | "library" // measured ahead of time (placements.json)
  | "manual" // a person placed it for the library (placements.overrides.json)
  | "detected" // found in the user's own photo, in the browser
  | "clicks"; // the user's clicks

export interface OnBodyPhoto {
  /** Library item id, or `file:<name>` for a photo from disk. */
  key: string;
  label: string;
  /** Null for a photo from disk (the viewer holds it as an ImageBitmap). */
  url: string | null;
  fromFile: boolean;
  w: number;
  h: number;
  /** Every hand found (placements v2), the one to wear the ring on first. */
  hands: PlacedHand[];
  handsBy: PlaceMethod;
  /** The neck, when the photo has one. */
  neck: OnBodyAnchor | null;
  neckBy: PlaceMethod;
  /** Detection already ran on this photo (once is enough). */
  detectTried?: boolean;
  /** A clicked finger whose width the photo's pixels could not give (the
   *  browser would not let us read them): the panel offers a width slider. */
  widthUnmeasured?: boolean;
}

export interface ManualState {
  kind: OnBodyKind;
  /** Clicked points so far, in photo PIXELS. */
  points: Vec2[];
  /** Clicks this placement takes (`CLICKS`: a finger 2, a neck 3). */
  needed: number;
  /** The backdrop framing the clicks are converted through. */
  framing: Required<BackgroundImageFraming>;
}

export interface OnBodyStatus {
  tone: "ok" | "info" | "warn";
  text: string;
}

export interface OnBodyState {
  kind: OnBodyKind;
  /** The user picked Ring/Necklace — a guess from the model no longer applies. */
  kindChosen: boolean;
  photo: OnBodyPhoto | null;
  /** The viewer is in On Body (`placeOnBody` succeeded and nothing cleared it). */
  placed: boolean;
  fit: Record<OnBodyKind, number>;
  /** Which of `photo.hands` the ring is on ("Switch Hand"). */
  handIndex: number;
  /** Which finger ("Thumb · Index · Middle · Ring · Pinky"). */
  finger: FingerName;
  /** Where along the finger, 0..2 from the knuckle (iJewel's "Along Finger"). */
  along: number;
  /** The stone faces the camera whatever the hand does (viewer ≥ v64). */
  headToCamera: boolean;
  /** Contact shadow on the skin, 0..1 (viewer ≥ v64). */
  contactShadow: number;
  /** A multiple of an unmeasured clicked finger's width (the width slider). */
  widthScale: number;
  /** The model container's transform, as the viewer reports it. */
  node: NodeTransform | null;
  /** The container's scale right after placement — "Model size" is relative to it. */
  baseScale: number;
  /** The hole centre in container-local units: rotations and size keep it fixed. */
  pivotLocal: Vec3 | null;
  /** A sensible Position step for this piece's size, world units. */
  posStep: number;
  /** Adjust is open and the finger tube is tinted. */
  setup: boolean;
  manual: ManualState | null;
  busy: null | "photo" | "detect" | "place" | "clear";
  status: OnBodyStatus | null;
  error: string | null;
}

export const FIT_DEFAULT: Record<OnBodyKind, number> = { finger: 1.04, neck: 1.15 };
export const FIT_RANGE: Record<OnBodyKind, { min: number; max: number }> = {
  finger: { min: 0.85, max: 1.25 },
  neck: { min: 0.9, max: 1.6 },
};
/** `BodyOccluderConfig.contactShadow`'s default in the viewer. */
export const CONTACT_SHADOW_DEFAULT = 0.5;
/** The unmeasured-width slider's range, × the estimate from the bone. */
export const WIDTH_SCALE_RANGE = { min: 0.6, max: 1.6 } as const;

const initial = (): OnBodyState => ({
  kind: "finger",
  kindChosen: false,
  photo: null,
  placed: false,
  fit: { ...FIT_DEFAULT },
  handIndex: 0,
  finger: "ring",
  along: ALONG_DEFAULT,
  headToCamera: false,
  contactShadow: CONTACT_SHADOW_DEFAULT,
  widthScale: 1,
  node: null,
  baseScale: 1,
  pivotLocal: null,
  posStep: 0.01,
  setup: false,
  manual: null,
  busy: null,
  status: null,
  error: null,
});

/**
 * The panel's half of a placed session, as a saved scene carries it (the
 * host block of a v65 scene file — `ui/editor/scene`). The viewer's half (the
 * placement, the occluder, the backdrop photo) travels in the scene itself;
 * this is only what the panel needs to show that photo again and keep
 * adjusting it: which photo and hand, the finger, the sliders, and the
 * Adjust readouts' reference points. The photo's pixels never travel.
 */
export interface OnBodyHost {
  kind: OnBodyKind;
  photo: Pick<OnBodyPhoto, "key" | "label" | "url" | "fromFile" | "w" | "h" | "hands" | "handsBy" | "neck" | "neckBy" | "widthUnmeasured">;
  handIndex: number;
  finger: FingerName;
  along: number;
  fit: Record<OnBodyKind, number>;
  headToCamera: boolean;
  contactShadow: number;
  widthScale: number;
  baseScale: number;
  pivotLocal: Vec3 | null;
  posStep: number;
}

/** How an On Body session frames its photo: whole, letterboxed. */
export const ON_BODY_FRAMING: Required<BackgroundImageFraming> = { ...DEFAULT_BACKGROUND_FRAMING, fit: "contain" };

/** What the backdrop was before On Body changed it. */
interface SavedBackdrop {
  source: "color" | "image" | "env" | "transparent";
  color: string | null;
  url: string | null;
  framing: BackgroundImageFraming | null;
}

const errText = (e: unknown, fallback: string) => (e instanceof Error && e.message ? e.message : fallback);

const FINGER_WORD: Record<FingerName, string> = { thumb: "thumb", index: "index finger", middle: "middle finger", ring: "ring finger", pinky: "little finger" };

export function statusFor(kind: OnBodyKind, method: PlaceMethod, finger: FingerName = "ring"): OnBodyStatus {
  switch (method) {
    case "manual":
      return { tone: "info", text: "Placed by hand — adjust if needed" };
    case "clicks":
      return { tone: "info", text: "Placed where you clicked — adjust if needed" };
    case "detected":
      return { tone: "info", text: `Found the ${FINGER_WORD[finger]} — adjust if needed` };
    default:
      return { tone: "ok", text: kind === "neck" ? "On the neck" : `On the ${FINGER_WORD[finger]}` };
  }
}

/** The prompt for each click of manual placement. */
export function manualPrompt(kind: OnBodyKind, step: number, finger: FingerName = "ring"): string {
  const steps =
    kind === "finger"
      ? [`Click the knuckle at the base of the ${FINGER_WORD[finger]}`, "Now click its middle joint"]
      : ["Click the base of the neck, at the front", "Now click the chin", "Now click one side of the neck"];
  return steps[Math.min(step, steps.length - 1)];
}

/** A Position step of ~1/20 of the hole's radius, rounded to 1, 2 or 5. */
function stepFor(innerRadius: number | undefined): number {
  if (!(innerRadius && innerRadius > 0)) return 0.01;
  const raw = innerRadius / 20;
  const p = 10 ** Math.floor(Math.log10(raw));
  const m = raw / p;
  return (m < 1.5 ? 1 : m < 3.5 ? 2 : 5) * p;
}

export class OnBodySession {
  private state: OnBodyState = initial();
  private listeners = new Set<() => void>();
  private saved: SavedBackdrop | null = null;
  /** The photo from disk, kept for detection when the user switches to Ring. */
  private file: File | null = null;
  /** The current photo's pixels for the two-click width, read once per photo
   *  (null: the browser would not hand them over). */
  private pixels: { key: string; px: Promise<PhotoPixels | null> } | null = null;
  /** Latest-wins token for photo picks, kind switches and Clear. */
  private token = 0;
  /** The set of models the placement was made for. */
  private modelIds = "";
  /** A `modelsChanged` arrived mid-operation. */
  private modelsDirty = false;
  /** Transform writes are applied in order, each from the latest readout. */
  private queue: Promise<unknown> = Promise.resolve();
  private placeTimer: number | null = null;
  private readonly onBodyV63: boolean;
  private readonly onBodyV64: boolean;
  private readonly framingV49: boolean;

  constructor(private readonly client: ViewerClient) {
    const caps = capsFor(client.contractVersion);
    this.onBodyV63 = caps.onBodyV63;
    this.onBodyV64 = caps.onBodyV64;
    this.framingV49 = caps.backgroundFramingV49;
    client.on("modelsChanged", () => void this.onModelsChanged());
    client.on("transformChanged", (p) => {
      if (p?.nodeId !== MODEL_ROOT_NODE_ID || !this.state.placed || this.state.busy === "clear") return;
      this.set({ node: { position: p.position, rotationDeg: p.rotationDeg, scale: p.scale } });
    });
  }

  private get r(): RingClientApi {
    return this.client.ring;
  }

  // ── store plumbing ─────────────────────────────────────────────────────────
  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  };
  getState = () => this.state;
  private set(patch: Partial<OnBodyState>) {
    this.state = { ...this.state, ...patch };
    for (const fn of this.listeners) fn();
  }

  /** The piece's kind as guessed from the model — ignored once the user has
   *  picked one, or while a photo is on (it would re-place under them). */
  suggestKind(kind: OnBodyKind) {
    if (!this.state.kindChosen && !this.state.photo && kind !== this.state.kind) this.set({ kind });
  }

  async setKind(kind: OnBodyKind) {
    if (kind === this.state.kind && this.state.kindChosen) return;
    const changed = kind !== this.state.kind;
    const tok = ++this.token;
    this.set({ kind, kindChosen: true, manual: null, error: null });
    // Switching is not asking to click: without an anchor for the new kind the
    // status says so, and Place by hand (or a photo that has one) is a click away.
    if (changed && this.state.photo) await this.run(tok, "place", () => this.placeCurrent(tok, false));
  }

  // ── photos ─────────────────────────────────────────────────────────────────

  /** A library photo: the backdrop, then the placement it ships with (or the
   *  user's clicks, for a photo the library has no placement for). */
  async pickPhoto(p: { id: string; label: string; url: string; placement: PhotoPlacement | null }) {
    const tok = ++this.token;
    this.file = null;
    this.set({ manual: null });
    await this.run(tok, "photo", async () => {
      await this.saveBackdrop();
      await this.r.setBackgroundImageUrl(p.url);
      if (tok !== this.token) return;
      const pl = p.placement;
      const size = pl ? { w: pl.w, h: pl.h } : await measureImage(p.url).catch(() => ({ w: 1, h: 1 }));
      const hands = pl?.hands ?? [];
      this.set({
        photo: {
          key: p.id,
          label: p.label,
          url: p.url,
          fromFile: false,
          w: size.w,
          h: size.h,
          hands,
          handsBy: hands.length && !hands[0].image ? "manual" : "library",
          neck: pl?.neck ? toAnchor(pl.neck) : null,
          neckBy: "manual",
        },
        ...this.freshPlacement(),
      });
      await this.placeCurrent(tok);
    });
  }

  /** A photo from disk: the backdrop, then detection (ring) or clicks. */
  async useOwnPhoto(file: File) {
    const tok = ++this.token;
    this.set({ manual: null });
    await this.run(tok, "photo", async () => {
      await this.saveBackdrop();
      await this.r.setBackgroundImageFile(file);
      if (tok !== this.token) return;
      const bmp = await createImageBitmap(file);
      const size = { w: bmp.width, h: bmp.height };
      bmp.close();
      this.file = file;
      this.set({
        photo: {
          key: `file:${file.name}:${file.size}`,
          label: file.name,
          url: null,
          fromFile: true,
          ...size,
          hands: [],
          handsBy: "detected",
          neck: null,
          neckBy: "clicks",
        },
        ...this.freshPlacement(),
      });
      await this.placeCurrent(tok);
    });
  }

  /** A new photo starts on its first hand, at the default seat; the finger,
   *  fit and look choices carry over. */
  private freshPlacement(): Partial<OnBodyState> {
    return { handIndex: 0, along: ALONG_DEFAULT, widthScale: 1 };
  }

  /** The hand the ring is on, and the finger — the chosen one when this hand
   *  has it, else the first it has (a hand-placed entry has one finger). */
  private handAndFinger(): { hand: PlacedHand; finger: FingerName } | null {
    const { photo, handIndex, finger } = this.state;
    const hand = photo?.hands[handIndex] ?? photo?.hands[0];
    if (!hand) return null;
    const have = fingersOf(hand);
    const f = have.includes(finger) ? finger : have[0];
    return f ? { hand, finger: f } : null;
  }

  /** What `placeOnBody` gets for the current kind, or null when there is none. */
  anchorNow(): { anchor: OnBodyAnchor; method: PlaceMethod; finger: FingerName } | null {
    const { photo, kind } = this.state;
    if (!photo) return null;
    if (kind === "neck") return photo.neck ? { anchor: photo.neck, method: photo.neckBy, finger: "ring" } : null;
    const hf = this.handAndFinger();
    if (!hf) return null;
    const a = fingerAnchor(photo, hf.hand, hf.finger, this.state.along);
    if (!a) return null;
    const anchor = this.state.widthScale !== 1 ? { ...a, width: a.width * this.state.widthScale } : a;
    return { anchor, method: photo.handsBy, finger: hf.finger };
  }

  /** Place the current photo for the current kind, finding an anchor first
   *  when there is none: detection for a ring on a photo from disk, else —
   *  when the user just picked this photo (`ask`) — clicks. */
  private async placeCurrent(tok: number, ask = true) {
    let photo = this.state.photo;
    const kind = this.state.kind;
    if (!photo || !this.onBodyV63) return;
    if (kind === "finger" && !photo.hands.length && this.file && !photo.detectTried) {
      this.set({ busy: "detect", status: { tone: "info", text: "Looking for the hand…" } });
      let hands: PlacedHand[] = [];
      try {
        hands = (await detectHands(this.file)).hands;
      } catch (e) {
        console.warn("[onBody] hand detection failed", e);
      }
      if (tok !== this.token || this.state.photo !== photo) return;
      photo = { ...photo, detectTried: true, hands, handsBy: "detected" };
      this.set({ photo, handIndex: 0, busy: "place" });
    }
    if (!this.anchorNow()) {
      if (ask) await this.startManual(kind, true);
      else
        this.set({
          status: {
            tone: "info",
            text: kind === "neck" ? "No neck marked in this photo — pick one with a neck, or place it by hand." : "No hand marked in this photo — place it by hand.",
          },
        });
      return;
    }
    await this.place(tok);
  }

  /** (Re-)place from the current anchor. Also "Auto fit": every call fits from
   *  scratch, so a nudge is dropped. */
  async place(tok = this.token) {
    const { photo, kind } = this.state;
    const a = this.anchorNow();
    if (!photo || !a || !this.onBodyV63) return;
    const res = await this.r.placeOnBody({
      kind,
      photo: { w: photo.w, h: photo.h },
      anchor: a.anchor,
      fit: this.state.fit[kind],
      ...(this.state.headToCamera ? { headToCamera: true } : {}),
    });
    if (tok !== this.token) return;
    if (!res) {
      this.set({ placed: false, node: null, status: { tone: "warn", text: "Add a piece to the scene — it goes on as soon as one loads." } });
      this.modelIds = "";
      return;
    }
    // The occluder keeps its own contact shadow across placements; say ours
    // once per placement so a fresh viewer agrees with the slider.
    if (this.onBodyV64 && res.occluder.contactShadow !== this.state.contactShadow) {
      await this.r.setBodyOccluder({ contactShadow: this.state.contactShadow }).catch(() => null);
    }
    const node: NodeTransform = { position: res.position, rotationDeg: res.rotationDeg, scale: res.scale };
    // The hole centre in the container's own units, measured AFTER the move —
    // the pivot every rotation and size edit keeps still.
    const fit = await this.r.getModelFit(kind).catch(() => null);
    if (tok !== this.token) return;
    this.set({
      placed: true,
      node,
      baseScale: res.scale.x || 1,
      pivotLocal: fit ? localOf(node, fit.holeCenter) : null,
      posStep: stepFor(fit?.innerRadius ?? res.fit.innerRadius),
      status: statusFor(kind, a.method, a.finger),
      error: null,
    });
    this.modelIds = await this.currentModelIds();
  }

  // ── Fit & Placement ────────────────────────────────────────────────────────

  /** "Switch Hand": the next hand found in the photo. */
  switchHand() {
    const n = this.state.photo?.hands.length ?? 0;
    if (n < 2) return;
    this.set({ handIndex: (this.state.handIndex + 1) % n });
    return this.placeNow();
  }

  setFinger(finger: FingerName) {
    if (finger === this.state.finger) return;
    this.set({ finger });
    return this.placeNow();
  }

  setAlong(v: number) {
    this.set({ along: v });
    this.schedulePlace();
  }

  setHeadToCamera(on: boolean) {
    this.set({ headToCamera: on });
    return this.placeNow();
  }

  /** The contact shadow is the occluder's own setting: no re-fit needed. */
  async setContactShadow(v: number) {
    this.set({ contactShadow: v });
    if (this.state.placed && this.onBodyV64) await this.r.setBodyOccluder({ contactShadow: v }).catch(() => null);
  }

  setWidthScale(v: number) {
    this.set({ widthScale: v });
    this.schedulePlace();
  }

  private placeNow() {
    if (this.placeTimer != null) window.clearTimeout(this.placeTimer);
    const tok = this.token;
    return this.run(tok, "place", () => this.place(tok));
  }

  // ── manual placement ───────────────────────────────────────────────────────

  async startManual(kind = this.state.kind, because = false) {
    if (!this.state.photo || !this.onBodyV63) return;
    const framing = this.framingV49
      ? fullFraming(await this.r.getBackgroundImageFraming().catch(() => null))
      : { ...DEFAULT_BACKGROUND_FRAMING };
    const why =
      kind === "neck" ? "No neck marked in this photo — " : this.state.photo.fromFile ? "Couldn’t find a hand — " : "No placement for this photo — ";
    this.set({
      manual: { kind, points: [], needed: CLICKS[kind], framing },
      status: { tone: "info", text: because ? `${why}click on the photo to place it` : "Click on the photo to place it" },
      error: null,
    });
    // Start reading the pixels while the user aims: the two-click width needs them.
    if (kind === "finger") void this.photoPixels();
  }

  cancelManual() {
    const { placed, kind } = this.state;
    const a = placed ? this.anchorNow() : null;
    this.set({ manual: null, status: a ? statusFor(kind, a.method, a.finger) : null });
  }

  /** The current photo's pixels, read once per photo; null when unreadable. */
  private photoPixels(): Promise<PhotoPixels | null> {
    const photo = this.state.photo;
    if (!photo) return Promise.resolve(null);
    if (this.pixels?.key !== photo.key) {
      const src = photo.fromFile ? this.file : photo.url;
      this.pixels = { key: photo.key, px: src ? readPhotoPixels(src) : Promise.resolve(null) };
    }
    return this.pixels.px;
  }

  /**
   * One click of manual placement, in photo PIXELS. A finger takes two — the
   * knuckle, then the middle joint — and its width is MEASURED from the photo
   * between them, the way the library was; a neck takes three (base, chin, one
   * side).
   */
  async manualPoint(pt: Vec2) {
    const m = this.state.manual;
    const photo = this.state.photo;
    if (!m || !photo) return;
    const points = [...m.points, pt];
    if (points.length < m.needed) {
      this.set({ manual: { ...m, points } });
      return;
    }
    const same = "The clicks landed on the same spot — start again.";
    if (m.kind === "neck") {
      const neck = manualNeckAnchor(points[0], points[1], points[2], photo.w, photo.h);
      if (!neck) return this.set({ manual: { ...m, points: [] }, error: same });
      const tok = ++this.token;
      this.set({ manual: null, kind: "neck", photo: { ...photo, neck, neckBy: "clicks" } });
      await this.run(tok, "place", () => this.place(tok));
      return;
    }
    const tok = ++this.token;
    this.set({ manual: null });
    await this.run(tok, "place", async () => {
      const px = await this.photoPixels();
      if (tok !== this.token) return;
      const k = px?.scale ?? 1;
      const scaled = (p: Vec2): Vec2 => [p[0] * k, p[1] * k];
      const fit = measureFingerBetween(px?.sampler ?? null, scaled(points[0]), scaled(points[1]));
      if (!fit) {
        this.set({ manual: { ...m, points: [] }, error: same });
        return;
      }
      const inPhotoPx = { ...fit, width: fit.width / k, widthSamples: fit.widthSamples.map((v) => v / k), shift: fit.shift / k };
      const hand = manualHand(points[0], points[1], inPhotoPx, photo.w, photo.h, this.state.finger);
      this.set({
        kind: "finger",
        handIndex: 0,
        widthScale: 1,
        photo: { ...photo, hands: [hand], handsBy: "clicks", widthUnmeasured: fit.method === "knuckle" },
      });
      await this.place(tok);
    });
  }

  // ── Adjust ─────────────────────────────────────────────────────────────────

  async openAdjust() {
    if (!this.state.placed) return;
    this.set({ setup: true });
    await this.r.setBodyOccluder({ showInSetup: true }).catch(() => null);
  }

  async closeAdjust() {
    this.set({ setup: false });
    if (this.state.placed) await this.r.setBodyOccluder({ showInSetup: false }).catch(() => null);
  }

  setFit(v: number) {
    const kind = this.state.kind;
    this.set({ fit: { ...this.state.fit, [kind]: v } });
    this.schedulePlace();
  }

  /** placeOnBody re-searches the hole and a slider streams: trailing debounce. */
  private schedulePlace() {
    if (this.placeTimer != null) window.clearTimeout(this.placeTimer);
    this.placeTimer = window.setTimeout(() => {
      this.placeTimer = null;
      const tok = this.token;
      void this.run(tok, "place", () => this.place(tok));
    }, 120);
  }

  autoFit() {
    const tok = this.token;
    return this.run(tok, "place", () => this.place(tok));
  }

  /** Apply a transform edit computed from the LATEST readout, then read back. */
  private edit(make: (cur: NodeTransform, pivot: Vec3) => NodeTransform) {
    this.queue = this.queue
      .then(async () => {
        const cur = this.state.node;
        if (!cur || !this.state.placed) return;
        const next = make(cur, this.state.pivotLocal ?? { x: 0, y: 0, z: 0 });
        this.set({ node: next });
        await this.r.setNodeTransform(MODEL_ROOT_NODE_ID, next);
        const back = await this.r.getNodeDetails(MODEL_ROOT_NODE_ID);
        if (back && this.state.placed) this.set({ node: { position: back.position, rotationDeg: back.rotationDeg, scale: back.scale } });
      })
      .catch((e) => this.set({ error: errText(e, "Could not move the piece.") }));
    return this.queue;
  }

  setPosition(axis: keyof Vec3, v: number) {
    return this.edit((cur) => ({ ...cur, position: { ...cur.position, [axis]: v } }));
  }

  setRotation(axis: keyof Vec3, deg: number) {
    return this.edit((cur, pivot) => aboutPivot(cur, pivot, { rotationDeg: { ...cur.rotationDeg, [axis]: deg } }));
  }

  /** Size as a multiple of the placed size; keeps the axes' ratios. */
  setSize(mult: number) {
    return this.edit((cur, pivot) => {
      const k = (mult * this.state.baseScale) / (cur.scale.x || 1);
      return aboutPivot(cur, pivot, { scale: { x: cur.scale.x * k, y: cur.scale.y * k, z: cur.scale.z * k } });
    });
  }

  rotate90(axis: keyof Vec3) {
    return this.edit((cur, pivot) => turnAboutWorldAxis(cur, pivot, axis, 90));
  }

  // ── Clear ──────────────────────────────────────────────────────────────────

  /** Leave On Body and put the backdrop from before it back. */
  async clear() {
    const tok = ++this.token;
    if (this.placeTimer != null) window.clearTimeout(this.placeTimer);
    this.set({ manual: null, setup: false, error: null });
    let error: string | null = null;
    await this.run(tok, "clear", async () => {
      try {
        if (this.onBodyV63) await this.r.clearOnBody();
        error = await this.restoreBackdrop();
      } finally {
        this.saved = null;
        this.file = null;
        this.pixels = null;
        this.modelIds = "";
        const { kind, kindChosen, fit, finger, headToCamera, contactShadow } = this.state;
        this.state = { ...initial(), kind, kindChosen, fit, finger, headToCamera, contactShadow, busy: "clear" };
      }
    });
    if (error) this.set({ status: { tone: "warn", text: error } });
  }

  /** Capture the backdrop once per session, before the first change. */
  private async saveBackdrop() {
    if (this.saved) return;
    const st = await this.r.getSceneState();
    const source: SavedBackdrop["source"] =
      st.backgroundSource ?? (st.background ? "color" : st.showEnvAsBackground ? "env" : "transparent");
    const framing = this.framingV49 ? await this.r.getBackgroundImageFraming().catch(() => null) : null;
    this.saved = { source, color: st.background ?? null, url: st.backgroundImageUrl ?? null, framing };
    // A session starts on the WHOLE photo (contain, as iJewel shows it): a
    // cover fit crops the hand under the panels, and a zoom or pan left over
    // from a studio backdrop would crop it further. Photo framing switches it
    // to cover or a zoom; Clear puts the old framing back.
    if (this.framingV49) await this.r.setBackgroundImageFraming({ ...ON_BODY_FRAMING }).catch(() => null);
  }

  /**
   * Put the saved backdrop back — but only while ours is still the one on
   * screen: a user who picked another background meanwhile keeps it. Returns a
   * message when the old backdrop cannot come back exactly.
   */
  private async restoreBackdrop(): Promise<string | null> {
    const saved = this.saved;
    const photo = this.state.photo;
    if (!saved || !photo) return null;
    const st = await this.r.getSceneState();
    const live = st.backgroundImageUrl ?? null;
    const ours =
      st.backgroundSource === undefined ||
      (st.backgroundSource === "image" && (photo.fromFile ? !live || live.startsWith("data:") : live === photo.url));
    if (!ours) return null;
    let note: string | null = null;
    switch (saved.source) {
      case "image":
        if (saved.url) await this.r.setBackgroundImageUrl(saved.url);
        else {
          await this.r.setTransparentBackground();
          note = "The earlier backdrop was a picture too large to keep, so the background is now clear.";
        }
        break;
      case "color":
        if (saved.color) await this.r.setBackground(saved.color);
        else await this.r.setTransparentBackground();
        break;
      case "env":
        await this.r.setShowEnvAsBackground(true);
        break;
      default:
        await this.r.setTransparentBackground();
    }
    if (saved.framing && this.framingV49) await this.r.setBackgroundImageFraming(saved.framing).catch(() => null);
    return note;
  }

  // ── saved scenes ───────────────────────────────────────────────────────────

  /** What a saved scene keeps of this session; null when nothing is on the body. */
  hostSnapshot(): OnBodyHost | null {
    const s = this.state;
    if (!s.placed || !s.photo) return null;
    const { key, label, url, fromFile, w, h, hands, handsBy, neck, neckBy, widthUnmeasured } = s.photo;
    return {
      kind: s.kind,
      photo: { key, label, url, fromFile, w, h, hands, handsBy, neck, neckBy, ...(widthUnmeasured ? { widthUnmeasured } : {}) },
      handIndex: s.handIndex,
      finger: s.finger,
      along: s.along,
      fit: { ...s.fit },
      headToCamera: s.headToCamera,
      contactShadow: s.contactShadow,
      widthScale: s.widthScale,
      baseScale: s.baseScale,
      pivotLocal: s.pivotLocal,
      posStep: s.posStep,
    };
  }

  /**
   * Take over what a saved scene just restored. The viewer has already done
   * its half — opening a scene clears On Body and puts back the scene's own —
   * so this writes nothing to the viewer: it reads the placement back and
   * shows the panel as it was saved. `null` = the scene has nothing on the
   * body; the panel lets go of its photo (keeping the user's choices).
   *
   * The backdrop from BEFORE the session is not in the file, so Clear after
   * this leaves the photo as the backdrop rather than guessing one.
   */
  async adoptHost(h: OnBodyHost | null) {
    const tok = ++this.token;
    if (this.placeTimer != null) window.clearTimeout(this.placeTimer);
    this.saved = null;
    this.file = null;
    this.pixels = null;
    this.modelsDirty = false;
    if (!h || !this.onBodyV63) {
      if (!this.state.photo && !this.state.placed) return;
      const { kind, kindChosen, fit, finger, headToCamera, contactShadow } = this.state;
      this.modelIds = "";
      this.state = { ...initial(), kind, kindChosen, fit, finger, headToCamera, contactShadow };
      for (const fn of this.listeners) fn();
      return;
    }
    this.set({
      kind: h.kind,
      kindChosen: true,
      photo: { ...h.photo },
      handIndex: h.handIndex,
      finger: h.finger,
      along: h.along,
      fit: { ...h.fit },
      headToCamera: h.headToCamera,
      contactShadow: h.contactShadow,
      widthScale: h.widthScale,
      baseScale: h.baseScale,
      pivotLocal: h.pivotLocal,
      posStep: h.posStep,
      setup: false,
      manual: null,
      busy: null,
      error: null,
    });
    const placed = (await this.r.getBodyOccluder().catch(() => null)) != null;
    if (tok !== this.token) return;
    const ids = await this.currentModelIds();
    if (tok !== this.token) return;
    if (!placed) {
      // The scene could not place it (no piece yet): it goes on when one loads.
      this.modelIds = "";
      if (ids) await this.run(tok, "place", () => this.place(tok));
      else this.set({ placed: false, node: null, status: { tone: "warn", text: "Add a piece to the scene — it goes on as soon as one loads." } });
      return;
    }
    const back = await this.r.getNodeDetails(MODEL_ROOT_NODE_ID).catch(() => null);
    if (tok !== this.token) return;
    const a = this.anchorNow();
    this.modelIds = ids;
    this.set({
      placed: true,
      node: back ? { position: back.position, rotationDeg: back.rotationDeg, scale: back.scale } : null,
      status: a ? statusFor(h.kind, a.method, a.finger) : null,
    });
  }

  // ── framing (the photo's own fit / zoom / pan) ─────────────────────────────

  /** Re-frame the photo and, since the anchor moved with it, re-place. */
  async frame(patch: Partial<BackgroundImageFraming>): Promise<BackgroundImageFraming | null> {
    if (!this.framingV49) return null;
    const out = await this.r.setBackgroundImageFraming(patch);
    if (this.state.placed) this.schedulePlace();
    return out;
  }

  // ── events ─────────────────────────────────────────────────────────────────

  private async currentModelIds(): Promise<string> {
    const ms = await this.r.listModels().catch(() => []);
    return ms.map((m) => m.id).sort().join(",");
  }

  /** A piece came or went: put the new one on the same anchor. (The viewer
   *  re-fits on a swap too; this keeps the readouts and the pivot honest, and
   *  places a piece that arrives AFTER the photo was picked.) */
  private async onModelsChanged() {
    if (!this.onBodyV63 || !this.anchorNow()) return;
    // Mid-operation the operation places for itself — but a piece that lands
    // while a photo is still decoding must not be forgotten: look again after.
    if (this.state.busy) {
      this.modelsDirty = true;
      return;
    }
    const ids = await this.currentModelIds();
    if (ids === this.modelIds) return;
    this.modelIds = ids;
    if (!ids) {
      this.set({ placed: false, node: null, status: { tone: "warn", text: "Add a piece to the scene — it goes on as soon as one loads." } });
      return;
    }
    const tok = this.token;
    await this.run(tok, "place", () => this.place(tok));
  }

  /** Run one operation with a busy flag and an error line. */
  private async run(tok: number, busy: NonNullable<OnBodyState["busy"]>, fn: () => Promise<void>) {
    this.set({ busy, error: null });
    try {
      await fn();
    } catch (e) {
      if (tok === this.token) this.set({ error: errText(e, "On Body could not do that.") });
    } finally {
      if (tok === this.token) {
        this.set({ busy: null });
        if (this.modelsDirty) {
          this.modelsDirty = false;
          void this.onModelsChanged();
        }
      }
    }
  }
}

const sessions = new WeakMap<ViewerClient, OnBodySession>();

/** The session for this viewer — a new viewer (iframe reload) starts fresh. */
export function onBodySessionFor(client: ViewerClient): OnBodySession {
  let s = sessions.get(client);
  if (!s) {
    s = new OnBodySession(client);
    sessions.set(client, s);
  }
  return s;
}

/** The session for this viewer if one was ever started — reading it creates nothing. */
export function onBodySessionIfAny(client: ViewerClient): OnBodySession | null {
  return sessions.get(client) ?? null;
}

export function useOnBody(session: OnBodySession): OnBodyState {
  return useSyncExternalStore(session.subscribe, session.getState);
}
